import {useEffect} from 'react'
import {Banner, Link} from '@primer/react'
import type {QueryKey} from '@github-ui/react-query'
import {useAddBanner, useDismissBanner, type InitialBanner} from '@github-ui/banner-stack'
import {useWorkflowEnable} from '../../hooks/use-workflow-enable'
import {useActionsAnalytics} from '../../hooks/use-actions-analytics'
import {
  type DisabledWorkflowBanner as DisabledWorkflowBannerData,
  DisabledWorkflowBannerVariant,
} from '@github-ui/dotcom-schema/types/actions/v1/workflow_runs_route'

export const DISABLED_WORKFLOW_BANNER_ID = 'disabled-workflow-banner'

type DisabledWorkflowBanner = InitialBanner & {'data-test-selector'?: string}

interface DisabledWorkflowBannerOptions extends DisabledWorkflowBannerData {
  queryKey: QueryKey
  owner: string
  repo: string
}

function DisabledWorkflowBannerPrimaryAction({
  variant,
  enablePath,
  queryKey,
  owner,
  repo,
  workflowId,
}: Pick<DisabledWorkflowBannerOptions, 'variant' | 'enablePath' | 'queryKey' | 'owner' | 'repo' | 'workflowId'>) {
  const enablesWorkflow = useWorkflowEnable({queryKey, path: enablePath, owner, repo, workflowId})
  const track = useActionsAnalytics()
  return (
    <Banner.PrimaryAction
      loading={enablesWorkflow.isPending}
      onClick={() => {
        track('workflow_banner.enable_workflow', 'ref_loc:disabled_workflow_banner')
        enablesWorkflow.mutate()
      }}
      loadingAnnouncement="Enabling workflow"
    >
      {variant === DisabledWorkflowBannerVariant.CHURNING ? 'Continue running workflow' : 'Enable workflow'}
    </Banner.PrimaryAction>
  )
}

function disabledWorkflowBannerTitle({
  variant,
  repositoryInactivityThresholdDays,
}: Pick<DisabledWorkflowBannerData, 'variant' | 'repositoryInactivityThresholdDays'>): string {
  switch (variant) {
    case DisabledWorkflowBannerVariant.DISABLED_FORK:
      return 'This scheduled workflow is disabled because scheduled workflows are disabled by default in forks.'
    case DisabledWorkflowBannerVariant.DISABLED_INACTIVITY:
      return `This scheduled workflow is disabled because there hasn't been activity in this repository for at least ${repositoryInactivityThresholdDays} days. Enable this workflow to resume scheduled runs.`
    case DisabledWorkflowBannerVariant.DISABLED_MANUALLY:
      return 'This workflow was disabled manually.'
    case DisabledWorkflowBannerVariant.DISABLED_ADMIN:
      return 'GitHub Actions is currently disabled for this workflow.'
    case DisabledWorkflowBannerVariant.CHURNING:
      return "This workflow will be disabled soon because there's no recent activity in the repository."
    case DisabledWorkflowBannerVariant.UNSPECIFIED:
      throw new Error('Disabled workflow banner variant is unspecified')
    default:
      throw new Error(`Unknown disabled workflow banner variant: ${variant}`)
  }
}

function disabledWorkflowBannerDescription({
  variant,
  supportUrl,
}: Pick<DisabledWorkflowBannerData, 'variant' | 'supportUrl'>) {
  if (variant !== DisabledWorkflowBannerVariant.DISABLED_ADMIN) return undefined

  return (
    <>
      Please reach out to{' '}
      <Link inline href={supportUrl}>
        GitHub Support
      </Link>{' '}
      for assistance.
    </>
  )
}

export function getDisabledWorkflowBanner({
  variant,
  canEnable,
  enablePath,
  repositoryInactivityThresholdDays,
  supportUrl,
  queryKey,
  owner,
  repo,
  workflowId,
}: DisabledWorkflowBannerOptions): DisabledWorkflowBanner {
  return {
    id: DISABLED_WORKFLOW_BANNER_ID,
    title: disabledWorkflowBannerTitle({variant, repositoryInactivityThresholdDays}),
    description: disabledWorkflowBannerDescription({variant, supportUrl}),
    variant: 'warning',
    'data-test-selector': 'disabled_workflow_banner',
    primaryAction: canEnable ? (
      <DisabledWorkflowBannerPrimaryAction
        variant={variant}
        enablePath={enablePath}
        queryKey={queryKey}
        owner={owner}
        repo={repo}
        workflowId={workflowId}
      />
    ) : undefined,
  }
}

export function useDisabledWorkflowBanner({
  data,
  queryKey,
  owner,
  repo,
}: {
  data?: DisabledWorkflowBannerData
  queryKey: QueryKey
  owner: string
  repo: string
}) {
  const addBanner = useAddBanner()
  const dismissBanner = useDismissBanner()

  useEffect(() => {
    if (!data) {
      dismissBanner(DISABLED_WORKFLOW_BANNER_ID)
      return
    }

    const id = addBanner(getDisabledWorkflowBanner({...data, queryKey, owner, repo}))

    return () => {
      dismissBanner(id)
    }
  }, [data, queryKey, owner, repo, addBanner, dismissBanner])
}
